import { createContext, useCallback, useContext, useEffect, useState } from "react";

import LoadingFallback from "@/components/Loading";
import {
  DEFAULT_LANG,
  TVars,
  getStoredLang,
  loadDict,
  setActiveDict,
  subscribe,
  translate,
} from "@/lib/i18n";
import { useSelector } from "@/store";
import { handleError } from "@/utils";

type I18nContextValue = {
  lang: string;
  t: (key: string, vars?: TVars) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

type Props = {
  children: React.ReactNode;
};

// Loads the active language dictionary and re-renders consumers when it
// changes. The selected language follows (in priority order) the logged-in
// user's saved preference, then the localStorage slot the LanguageSelector
// writes, then the default. The LanguageSelector fires a `languagechange`
// event after writing localStorage, which we listen for here.
export default function I18nProvider({ children }: Props) {
  const userLang = useSelector((state) => state.auth.user?.language);
  const [lang, setLang] = useState<string>(() => userLang || getStoredLang());
  const [ready, setReady] = useState<boolean>(false);
  const [version, setVersion] = useState<number>(0);

  // Mirror the user's saved language (e.g. after login) into the active lang.
  useEffect(() => {
    if (userLang && userLang !== lang) setLang(userLang);
  }, [userLang]);

  // The LanguageSelector persists to localStorage then dispatches this event.
  useEffect(() => {
    const onChange = () => setLang(getStoredLang());
    window.addEventListener("languagechange", onChange);

    return () => window.removeEventListener("languagechange", onChange);
  }, []);

  // Fetch and activate the dictionary whenever the language changes. Falls back
  // to the default language file if a non-default one fails to load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dict = await loadDict(lang);
        if (!cancelled) setActiveDict(lang, dict);
      } catch (error) {
        handleError(error);
        if (lang !== DEFAULT_LANG) {
          try {
            const fallback = await loadDict(DEFAULT_LANG);
            if (!cancelled) setActiveDict(DEFAULT_LANG, fallback);
          } catch (fallbackError) {
            handleError(fallbackError);
          }
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang]);

  // Re-render this subtree whenever the active dictionary swaps. `version`
  // bumps on every dictionary change and, together with `lang`, gives `t` a
  // fresh identity so memoized consumers (DataGrid columns, useMemo'd field
  // configs) that list `t` in their deps recompute with the new strings.
  useEffect(() => subscribe(() => setVersion((v) => v + 1)), []);

  const t = useCallback((key: string, vars?: TVars) => translate(key, vars), [lang, version]);

  if (!ready) return <LoadingFallback />;

  return <I18nContext.Provider value={{ lang, t }}>{children}</I18nContext.Provider>;
}

export const useTranslation = (): I18nContextValue => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useTranslation must be used within an I18nProvider");
  }

  return ctx;
};
